import { Box, Card, CardContent, Skeleton, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AddPaymentMethodButton } from '../../add-payment-method';
import { useFreeUsage } from '../api/useFreeUsage';
import { UsageProgress } from './UsageProgress';
import { UsageHistoryTable } from './UsageHistoryTable';

type Props = {
  onPortalError?: (error: Error) => void;
};

export const UsageSection = ({ onPortalError }: Props = {}) => {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useFreeUsage();

  if (isLoading) {
    return (
      <Card variant="outlined" sx={{ mt: 4 }}>
        <CardContent>
          <Stack spacing={2}>
            <Skeleton variant="text" width="40%" height={32} />
            <Skeleton variant="rectangular" height={8} />
            <Skeleton variant="rectangular" height={160} />
          </Stack>
        </CardContent>
      </Card>
    );
  }

  if (isError || !data) {
    return null;
  }

  const addPaymentMethodButton = (
    <AddPaymentMethodButton variant="contained" color="primary" onMutationError={onPortalError} />
  );

  return (
    <Card variant="outlined" sx={{ mt: 4 }}>
      <CardContent>
        <Stack spacing={3}>
          <Typography variant="h6">{t('subscription.usage.title')}</Typography>

          <UsageProgress
            allowance={data.allowance}
            used={data.used}
            remaining={data.remaining}
            unlimited={data.unlimited}
            paymentMethodOnFile={data.paymentMethodOnFile}
            meteredRatePerRo={3}
            addPaymentMethodButton={addPaymentMethodButton}
          />

          <Box>
            {/* Rendered as a <p> rather than the default <h6> so it doesn't compete
                with the dedicated <BillingHistoryTable>'s "Billing history" heading
                in the page's accessibility tree. */}
            <Typography variant="subtitle1" component="p" sx={{ mb: 1 }}>
              {t('subscription.usage.historyTitle')}
            </Typography>
            <UsageHistoryTable periods={data.periods} />
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};
